import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { LocalizationProvider } from "./hooks/useLocalization";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ScrollToTop } from "./components/ScrollToTop";
import { GlobalDatePickerFix } from "./components/GlobalDatePickerFix";
import { Toaster } from "./components/ui/sonner";

import { AuthLayout } from "./layouts/AuthLayout";
import { ManagementLayout } from "./layouts/ManagementLayout";
import { EmployeeLayout } from "./layouts/EmployeeLayout";

import LoginPage from "./pages/auth/LoginPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import ChangePasswordPage from "./pages/auth/ChangePasswordPage";
import StaffActivatePage from "./pages/auth/StaffActivatePage";
import MfaSetupPage from "./pages/auth/MfaSetupPage";
import MfaVerifyPage from "./pages/auth/MfaVerifyPage";
import HomePage from "./pages/public/HomePage";
import NotFoundPage from "./pages/NotFoundPage";

import DashboardPage from "./pages/management/DashboardPage";
import MyAccountPage from "./pages/management/MyAccountPage";
import EmployeeDirectoryPage from "./pages/management/EmployeeDirectoryPage";
import EmployeeCreatePage from "./pages/management/EmployeeCreatePage";
import EmployeeTestDataReviewPage from "./pages/management/EmployeeTestDataReviewPage";
import EmployeeProfilePage from "./pages/management/EmployeeProfilePage";
import OnboardingQueuePage from "./pages/management/OnboardingQueuePage";
import OnboardingCasePage from "./pages/management/OnboardingCasePage";
import ContractTemplatesPage from "./pages/management/ContractTemplatesPage";
import PolicyLibraryPage from "./pages/management/PolicyLibraryPage";
import AuditLogPage from "./pages/management/AuditLogPage";
import SystemJobsPage from "./pages/management/SystemJobsPage";
import ProductionReadinessPage from "./pages/management/ProductionReadinessPage";
import SecuritySettingsPage from "./pages/management/SecuritySettingsPage";
import StoreAccessPage from "./pages/management/StoreAccessPage";
import EmployeeStoreAssignmentReviewPage from "./pages/management/EmployeeStoreAssignmentReviewPage";
import DocumentReviewQueuePage from "./pages/management/DocumentReviewQueuePage";
import ComplianceDashboardPage from "./pages/management/ComplianceDashboardPage";
import Article36DashboardPage from "./pages/management/Article36DashboardPage";
import Article36AgreementDetailPage from "./pages/management/Article36AgreementDetailPage";
import ApplicationsQueuePage from "./pages/management/ApplicationsQueuePage";
import ApplicationWizardPage from "./pages/management/ApplicationWizardPage";
import NoticeDetailPage from "./pages/management/NoticeDetailPage";
import ManagementUserDirectoryPage from "./pages/management/ManagementUserDirectoryPage";
import AccessProfilesPage from "./pages/management/AccessProfilesPage";
import ManagementUserCreatePage from "./pages/management/ManagementUserCreatePage";
import MigrationReviewPage from "./pages/management/MigrationReviewPage";
import MigrationProfileDetailPage from "./pages/management/MigrationProfileDetailPage";
import AvailabilityReviewPage from "./pages/management/AvailabilityReviewPage";
import ShiftTemplatesPage from "./pages/management/ShiftTemplatesPage";
import StaffingPlanPage from "./pages/management/StaffingPlanPage";
import ScheduleListPage from "./pages/management/ScheduleListPage";
import ScheduleBuilderPage from "./pages/management/ScheduleBuilderPage";
import ShiftSwapManagementPage from "./pages/management/ShiftSwapManagementPage";
import RecruitmentJobsPage from "./pages/management/RecruitmentJobsPage";
import RecruitmentApplicantsPage from "./pages/management/RecruitmentApplicantsPage";
import RecruitmentApplicantDetailPage from "./pages/management/RecruitmentApplicantDetailPage";
import InterviewSlotsPage from "./pages/management/InterviewSlotsPage";
import InterviewCalendarPage from "./pages/management/InterviewCalendarPage";
import InterviewAvailabilityPage from "./pages/management/InterviewAvailabilityPage";
import TimekeepingImportsPage from "./pages/management/TimekeepingImportsPage";
import TimekeepingMonthlyPage from "./pages/management/TimekeepingMonthlyPage";
import TimekeepingReviewPage from "./pages/management/TimekeepingReviewPage";
import PayrollDashboardPage from "./pages/management/PayrollDashboardPage";
import PayrollPeriodDetailPage from "./pages/management/PayrollPeriodDetailPage";
import PeriodPaymentsPage from "./pages/management/PeriodPaymentsPage";
import PayrollAdminPackagePage from "./pages/management/PayrollAdminPackagePage";
import PayrollLineDetailPage from "./pages/management/PayrollLineDetailPage";
import MedicalChecksPage from "./pages/management/MedicalChecksPage";
import MedicalComplianceDashboardPage from "./pages/management/MedicalComplianceDashboardPage";
import TrainingDashboardPage from "./pages/management/TrainingDashboardPage";
import TrainingSourceRegisterPage from "./pages/management/TrainingSourceRegisterPage";
import TrainingSourceDetailPage from "./pages/management/TrainingSourceDetailPage";
import TrainingContentReviewPage from "./pages/management/TrainingContentReviewPage";
import TrainingCoursesPage from "./pages/management/TrainingCoursesPage";
import TrainingCourseEditorPage from "./pages/management/TrainingCourseEditorPage";
import TrainingAssignmentsPage from "./pages/management/TrainingAssignmentsPage";
import TrainingPracticalReviewsPage from "./pages/management/TrainingPracticalReviewsPage";
import TrainingCertificationsPage from "./pages/management/TrainingCertificationsPage";
import LeaveDashboardPage from "./pages/management/LeaveDashboardPage";
import LeaveReviewPage from "./pages/management/LeaveReviewPage";
import LeavePoliciesPage from "./pages/management/LeavePoliciesPage";
import LeaveEmployeeDetailPage from "./pages/management/LeaveEmployeeDetailPage";
import LeaveMandatoryUsePage from "./pages/management/LeaveMandatoryUsePage";
import WorkCalendarsPage from "./pages/management/WorkCalendarsPage";
import WorkCalendarDetailPage from "./pages/management/WorkCalendarDetailPage";
import DriveIngestionPage from "./pages/management/DriveIngestionPage";
import EmailDeliveryPage from "./pages/management/EmailDeliveryPage";
import ActionCenterPage from "./pages/management/ActionCenterPage";
import DataQualityPage from "./pages/management/DataQualityPage";
import DataQualityFindingDetailPage from "./pages/management/DataQualityFindingDetailPage";
import WageHistoryTimelinePage from "./pages/management/WageHistoryTimelinePage";
import HealthCommitteeDashboardPage from "./pages/management/HealthCommitteeDashboardPage";
import HealthCommitteeMeetingsPage from "./pages/management/HealthCommitteeMeetingsPage";
import HealthCommitteeMeetingFormPage from "./pages/management/HealthCommitteeMeetingFormPage";
import HealthCommitteeMeetingDetailPage from "./pages/management/HealthCommitteeMeetingDetailPage";
import HealthCommitteeActionsPage from "./pages/management/HealthCommitteeActionsPage";
import HealthCommitteeMembersPage from "./pages/management/HealthCommitteeMembersPage";
import LongHourInterviewDashboardPage from "./pages/management/LongHourInterviewDashboardPage";
import LongHourInterviewPoliciesPage from "./pages/management/LongHourInterviewPoliciesPage";
import LongHourInterviewCandidatesPage from "./pages/management/LongHourInterviewCandidatesPage";
import LongHourInterviewCandidateDetailPage from "./pages/management/LongHourInterviewCandidateDetailPage";
import CorrectiveActionsPage from "./pages/management/CorrectiveActionsPage";
import SupplyPickupAssignmentsPage from "./pages/management/SupplyPickupAssignmentsPage";
import CorrectiveActionDetailPage from "./pages/management/CorrectiveActionDetailPage";

import CareersPage from "./pages/public/CareersPage";
import CareerJobDetailPage from "./pages/public/CareerJobDetailPage";
import ApplyPage from "./pages/public/ApplyPage";
import CareerApplicationStatusPage from "./pages/public/CareerApplicationStatusPage";

import EmployeeHomePage from "./pages/employee/EmployeeHomePage";
import MyOnboardingPage from "./pages/employee/MyOnboardingPage";
import MyProfilePage from "./pages/employee/MyProfilePage";
import MyDocumentsPage from "./pages/employee/MyDocumentsPage";
import MyContractPage from "./pages/employee/MyContractPage";
import MyPoliciesPage from "./pages/employee/MyPoliciesPage";
import MyNotificationsPage from "./pages/employee/MyNotificationsPage";
import MyArticle36VotePage from "./pages/employee/MyArticle36VotePage";
import MyAvailabilityPage from "./pages/employee/MyAvailabilityPage";
import MySchedulePage from "./pages/employee/MySchedulePage";
import MyPayrollPage from "./pages/employee/MyPayrollPage";
import MyTrainingPage from "./pages/employee/MyTrainingPage";
import TrainingCourseViewerPage from "./pages/employee/TrainingCourseViewerPage";
import MyCertificationsPage from "./pages/employee/MyCertificationsPage";
import MyLeavePage from "./pages/employee/MyLeavePage";
import MyLeaveHistoryPage from "./pages/employee/MyLeaveHistoryPage";
import MyLeaveRequestsPage from "./pages/employee/MyLeaveRequestsPage";
import MyMandatoryLeavePage from "./pages/employee/MyMandatoryLeavePage";

import "./App.css";

function App() {
  return (
    <AuthProvider>
      <LocalizationProvider>
        <BrowserRouter>
          <ScrollToTop />
          <GlobalDatePickerFix />
          <Routes>
            {/* Public homepage - never opens directly into a portal. Optional /en and /ja
                localized aliases set the language then render the same page. */}
            <Route path="/" element={<HomePage />} />
            <Route path="/en" element={<HomePage />} />
            <Route path="/ja" element={<HomePage />} />

            <Route path="/careers" element={<CareersPage />} />
            <Route path="/careers/jobs/:jobId" element={<CareerJobDetailPage />} />
            <Route path="/careers/jobs/:jobId/apply" element={<ApplyPage />} />
            <Route path="/careers/applications/:applicantId" element={<CareerApplicationStatusPage />} />

            <Route element={<AuthLayout />}>
              <Route path="/staff/login" element={<LoginPage />} />
              <Route path="/login" element={<Navigate to="/staff/login" replace />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/change-password" element={<ChangePasswordPage />} />
            </Route>

            <Route path="/staff/activate" element={<StaffActivatePage />} />
            <Route path="/staff/mfa/setup" element={<MfaSetupPage />} />
            <Route path="/staff/mfa/verify" element={<MfaVerifyPage />} />

            <Route
              path="/management"
              element={
                <ProtectedRoute area="management">
                  <ManagementLayout title="Management Dashboard" />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="account" element={<MyAccountPage />} />
              <Route path="employees" element={<EmployeeDirectoryPage />} />
              <Route path="employees/new" element={<EmployeeCreatePage />} />
              <Route path="employees/test-data-review" element={<EmployeeTestDataReviewPage />} />
              <Route path="employees/:employeeId" element={<EmployeeProfilePage />} />
              <Route path="employees/:employeeId/wage-history" element={<WageHistoryTimelinePage />} />
              <Route path="onboarding" element={<OnboardingQueuePage />} />
              <Route path="onboarding/:caseId" element={<OnboardingCasePage />} />
              <Route path="document-review" element={<DocumentReviewQueuePage />} />
              <Route path="contracts" element={<ContractTemplatesPage />} />
              <Route path="policies" element={<PolicyLibraryPage />} />
              <Route path="applications" element={<ApplicationsQueuePage />} />
              <Route path="applications/:applicationId" element={<ApplicationWizardPage />} />
              <Route path="compliance" element={<ComplianceDashboardPage />} />
              <Route path="article36" element={<Article36DashboardPage />} />
              <Route path="article36/:agreementId" element={<Article36AgreementDetailPage />} />
              <Route path="notices/:noticeId" element={<NoticeDetailPage />} />
              <Route path="audit" element={<AuditLogPage />} />
              <Route path="system/jobs" element={<SystemJobsPage />} />
              <Route path="system/production-readiness" element={<ProductionReadinessPage />} />
              <Route path="security/users" element={<ManagementUserDirectoryPage />} />
              <Route path="security/access-profiles" element={<AccessProfilesPage />} />
              <Route path="security/store-access" element={<StoreAccessPage />} />
              <Route path="employees/store-assignment-review" element={<EmployeeStoreAssignmentReviewPage />} />
              <Route path="security/users/new" element={<ManagementUserCreatePage />} />
              <Route path="security" element={<SecuritySettingsPage />} />
              <Route path="workforce/migration-review" element={<MigrationReviewPage />} />
              <Route path="workforce/migration-review/:employeeId" element={<MigrationProfileDetailPage />} />
              <Route path="workforce/availability-review" element={<AvailabilityReviewPage />} />
              <Route path="scheduling/shift-templates" element={<ShiftTemplatesPage />} />
              <Route path="scheduling/staffing-plan" element={<StaffingPlanPage />} />
              <Route path="scheduling/schedules" element={<ScheduleListPage />} />
              <Route path="scheduling/schedules/:scheduleId" element={<ScheduleBuilderPage />} />
              <Route path="scheduling/shift-swaps" element={<ShiftSwapManagementPage />} />
              <Route path="recruitment/jobs" element={<RecruitmentJobsPage />} />
              <Route path="recruitment/applicants" element={<RecruitmentApplicantsPage />} />
              <Route path="recruitment/applicants/:applicantId" element={<RecruitmentApplicantDetailPage />} />
              <Route path="recruitment/interview-slots" element={<InterviewSlotsPage />} />
              <Route path="recruitment/interviews/calendar" element={<InterviewCalendarPage />} />
              <Route path="recruitment/interview-availability" element={<InterviewAvailabilityPage />} />
              <Route path="timekeeping/imports" element={<TimekeepingImportsPage />} />
              <Route path="timekeeping/monthly" element={<TimekeepingMonthlyPage />} />
              <Route path="timekeeping/review/:employeeId" element={<TimekeepingReviewPage />} />
              <Route path="payroll" element={<PayrollDashboardPage />} />
              <Route path="payroll/periods/:periodId" element={<PayrollPeriodDetailPage />} />
              <Route path="payroll/periods/:periodId/payments" element={<PeriodPaymentsPage />} />
              <Route path="payroll/periods/:periodId/admin-package" element={<PayrollAdminPackagePage />} />
              <Route path="payroll/lines/:lineId" element={<PayrollLineDetailPage />} />
              <Route path="medical-checks" element={<MedicalChecksPage />} />
              <Route path="compliance/medical" element={<MedicalComplianceDashboardPage />} />
              <Route path="training" element={<TrainingDashboardPage />} />
              <Route path="training/sources" element={<TrainingSourceRegisterPage />} />
              <Route path="training/sources/:sourceId" element={<TrainingSourceDetailPage />} />
              <Route path="training/content-review" element={<TrainingContentReviewPage />} />
              <Route path="training/courses" element={<TrainingCoursesPage />} />
              <Route path="training/courses/:courseId" element={<TrainingCourseEditorPage />} />
              <Route path="training/assignments" element={<TrainingAssignmentsPage />} />
              <Route path="training/practical-reviews" element={<TrainingPracticalReviewsPage />} />
              <Route path="training/certifications" element={<TrainingCertificationsPage />} />
              <Route path="leave" element={<LeaveDashboardPage />} />
              <Route path="leave/review" element={<LeaveReviewPage />} />
              <Route path="leave/policies" element={<LeavePoliciesPage />} />
              <Route path="leave/mandatory-use" element={<LeaveMandatoryUsePage />} />
              <Route path="leave/employees/:employeeId" element={<LeaveEmployeeDetailPage />} />
              <Route path="settings/work-calendars" element={<WorkCalendarsPage />} />
              <Route path="settings/work-calendars/:calendarId" element={<WorkCalendarDetailPage />} />
              <Route path="timekeeping/drive" element={<DriveIngestionPage />} />
              <Route path="email/deliveries" element={<EmailDeliveryPage />} />
              <Route path="action-center" element={<ActionCenterPage />} />
              <Route path="action-center/monthly-digest" element={<ActionCenterPage />} />
              <Route path="data-quality" element={<DataQualityPage />} />
              <Route path="data-quality/findings/:findingId" element={<DataQualityFindingDetailPage />} />
              <Route path="compliance/health-committee" element={<HealthCommitteeDashboardPage />} />
              <Route path="compliance/health-committee/meetings" element={<HealthCommitteeMeetingsPage />} />
              <Route path="compliance/health-committee/meetings/new" element={<HealthCommitteeMeetingFormPage />} />
              <Route path="compliance/health-committee/meetings/:meetingId" element={<HealthCommitteeMeetingDetailPage />} />
              <Route path="compliance/health-committee/actions" element={<HealthCommitteeActionsPage />} />
              <Route path="compliance/health-committee/members" element={<HealthCommitteeMembersPage />} />
              <Route path="compliance/long-hour-interviews" element={<LongHourInterviewDashboardPage />} />
              <Route path="compliance/long-hour-interviews/policies" element={<LongHourInterviewPoliciesPage />} />
              <Route path="compliance/long-hour-interviews/candidates" element={<LongHourInterviewCandidatesPage />} />
              <Route path="compliance/long-hour-interviews/:candidateId" element={<LongHourInterviewCandidateDetailPage />} />
              <Route path="compliance/corrective-actions" element={<CorrectiveActionsPage />} />
              <Route path="operations/supply-pickup" element={<SupplyPickupAssignmentsPage />} />
              <Route path="compliance/corrective-actions/:caseId" element={<CorrectiveActionDetailPage />} />
            </Route>

            <Route
              path="/employee"
              element={
                <ProtectedRoute area="employee">
                  <EmployeeLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<EmployeeHomePage />} />
              <Route path="onboarding" element={<MyOnboardingPage />} />
              <Route path="profile" element={<MyProfilePage />} />
              <Route path="documents" element={<MyDocumentsPage />} />
              <Route path="contract" element={<MyContractPage />} />
              <Route path="policies" element={<MyPoliciesPage />} />
              <Route path="notifications" element={<MyNotificationsPage />} />
              <Route path="article36-vote" element={<MyArticle36VotePage />} />
              <Route path="availability" element={<MyAvailabilityPage />} />
              <Route path="schedule" element={<MySchedulePage />} />
              <Route path="payroll" element={<MyPayrollPage />} />
              <Route path="training" element={<MyTrainingPage />} />
              <Route path="training/courses/:courseId" element={<TrainingCourseViewerPage />} />
              <Route path="training/certifications" element={<MyCertificationsPage />} />
              <Route path="leave" element={<MyLeavePage />} />
              <Route path="leave/history" element={<MyLeaveHistoryPage />} />
              <Route path="leave/requests" element={<MyLeaveRequestsPage />} />
              <Route path="leave/mandatory-use" element={<MyMandatoryLeavePage />} />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </LocalizationProvider>
    </AuthProvider>
  );
}

export default App;
